import { themeTypeCrudConfig, type ThemeType } from '@/config/objects/theme-types';
import {
  CrudDetailView,
  PageContainer,
  useApiClient,
} from '@startupquickstart/frontend';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useNavigate, useParams } from 'react-router-dom';
import { toast } from 'sonner';

export function ThemeTypeDetailPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const apiClient = useApiClient();
  const queryClient = useQueryClient();

  const deleteMutation = useMutation({
    mutationFn: async (themeTypeId: string): Promise<{ success: boolean; error?: string }> => {
      return apiClient.delete(`/theme-types/${themeTypeId}`);
    },
    onSuccess: (response: { success: boolean; error?: string }) => {
      if (response.success) {
        queryClient.invalidateQueries({ queryKey: ['ThemeType'] });
        navigate('/theme-types');
      } else {
        throw new Error(response.error || 'Failed to delete theme type');
      }
    },
  });

  if (!id) {
    return (
      <PageContainer>
        <div className="text-center">
          <h2 className="text-xl font-semibold mb-2">Theme Type Not Found</h2>
          <p className="text-muted-foreground">The requested theme type could not be found.</p>
        </div>
      </PageContainer>
    );
  }

  const handleEdit = (themeType: ThemeType) => {
    navigate(`/theme-types/${themeType.id}/edit`);
  };

  const handleDelete = async (themeType: ThemeType) => {
    return toast.promise(deleteMutation.mutateAsync(themeType.id), {
      loading: 'Deleting theme type...',
      success: 'Theme type deleted successfully',
      error: (error: Error) => error?.message || 'Failed to delete theme type',
    });
  };

  const breadcrumbs = [
    { label: 'Home', href: '/' },
    { label: 'Theme Types', href: '/theme-types' },
    { label: 'Details', current: true },
  ];

  return (
    <PageContainer>
      <CrudDetailView
        config={themeTypeCrudConfig}
        itemId={id}
        breadcrumbs={breadcrumbs}
        onEdit={handleEdit}
        onDelete={handleDelete}
      />
    </PageContainer>
  );
}
